import { useEffect, useMemo } from "react";
import { useBannerContext } from "oolib";

import { useGetQueryData } from "../react-query-hooks/general";
import { __CheckAccessToContentType } from "../accessControl";
import { useCollabDoc } from "./useCollabDoc";
import { mergeCollabFieldsIntoContent } from "./mergeCollabFieldsIntoContent";
import { isContentCollabEnabled } from "./isContentCollabEnabled";

/**
 * Everything a published page needs to be a collab surface, in one hook:
 *
 *  - decides whether collab is ACTIVE for this doc: tenant flag on and not
 *    gated off on prod (isContentCollabEnabled) AND doc is published (collab
 *    never attaches to drafts/moderation clones — legacy machinery owns those)
 *  - decides whether this user may EDIT (PUBLISH || MODERATE on the
 *    contentType — same gate as okf-sub's authorize; viewers still get the
 *    live read-through)
 *  - runs the live session (useCollabDoc)
 *  - surfaces flush failures as a red alert banner (same visibility the
 *    legacy save path gives; the server retries every ~1s while the doc
 *    stays dirty, so the alert re-fires only on fresh failures)
 *
 * Returns:
 *  - liveContribution: the REST doc with live collab values overlaid
 *    (non-collab paths — tags, annotations — keep coming from the
 *    contribution query untouched). Falls back to the plain contribution
 *    when no session is active.
 *  - collabEditValue: the CollabEditContext value for ViewBlockGenerator
 *    (locks, setField, acquire/release...) — null until the session has
 *    synced, and null everywhere collab isn't active.
 *  - collabOwnsThisDoc: flag on + doc published — true even before the
 *    session syncs. This is the "legacy lifecycle is server-gated for this
 *    doc" signal (rejectLegacyLifecycleOnCollabTenants), used to hide the
 *    legacy Edit/Review entry points.
 *  - saveStatus: 'idle' | 'saving' | 'saved' | 'error' for OUR edits
 *    (path-level flush confirmations from the server).
 */
export const useContentDocCollabSession = ({ contentType, contribution }) => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const collabModeEnabled = isContentCollabEnabled(platformConfigs);

  const { user } = useGetQueryData("userData") || {};
  const canEdit =
    !!contentType &&
    (__CheckAccessToContentType(user, "PUBLISH", contentType) ||
      __CheckAccessToContentType(user, "MODERATE", contentType));

  const collabOwnsThisDoc =
    collabModeEnabled &&
    !!contribution?._id &&
    contribution?.kp_published_status === "published";

  const collab = useCollabDoc({
    enabled: collabOwnsThisDoc,
    contentType,
    contentId: contribution?._id,
  });

  // The session is expected but hasn't synced yet. Callers hold the doc in a
  // loading state until this clears: until the Y.Doc lands there is no
  // collabEditValue, so every field renders read-only and empty editable blocks
  // are suppressed — showing that and then flipping to the editable layout is
  // the read-only flash, and the reflow is what moves the page under a reader
  // who has already scrolled.
  // KNOWN GAP: if the sync engine is unreachable this never clears, so the
  // caller's loader spins until it connects. Failure signalling is a follow-up.
  const collabPending = collab.sessionExpected && !collab.synced;

  const { SET_ALERT_BANNER } = useBannerContext();
  useEffect(() => {
    if (collab.flushError) {
      SET_ALERT_BANNER({
        color: "red",
        msg: `Your latest edits could not be saved (${collab.flushError}). Retrying automatically — don't close the tab.`,
        timeOut: 5000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collab.flushError]);

  const liveContribution = useMemo(
    () =>
      collab.fields
        ? mergeCollabFieldsIntoContent({ content: contribution, fields: collab.fields })
        : contribution,
    [collab.fields, contribution]
  );

  const collabEditValue = useMemo(
    () =>
      collab.synced
        ? {
            canEdit,
            socketId: collab.socketId,
            locks: collab.locks,
            setField: collab.setField,
            acquireLock: collab.acquireLock,
            releaseLock: collab.releaseLock,
            hasLockClaim: collab.hasLockClaim,
          }
        : null,
    [
      collab.synced,
      canEdit,
      collab.socketId,
      collab.locks,
      collab.setField,
      collab.acquireLock,
      collab.releaseLock,
      collab.hasLockClaim,
    ]
  );

  return {
    liveContribution,
    collabEditValue,
    collabOwnsThisDoc,
    collabPending,
    saveStatus: collab.saveStatus,
  };
};
